<template>
  <div class="aldoc">
    <p class="aldoc__lead">
      판매업체 온보딩·일반회원 이용약관과 공급계약서의 최신 본문을 관리합니다. 구분(이용약관/계약서)과 버전을
      활성화하면 온보딩 화면에 바로 반영됩니다.
    </p>

    <section class="sui-card sui-card--flush" data-tour="admin-legal-docs-list">
      <div class="sui-toolbar" data-tour="admin-legal-docs-toolbar">
        <strong>약관 목록</strong>
        <label class="aldoc__filter">
          <span>대상</span>
          <select v-model="audience" @change="load">
            <option value="">전체</option>
            <option value="SELLER">판매업체</option>
            <option value="MEMBER">일반회원</option>
            <option value="ALL">공통</option>
          </select>
        </label>
        <span class="sui-toolbar__spacer" />
        <button
          type="button"
          class="sui-btn sui-btn--primary"
          data-tour="admin-legal-docs-create"
          @click="openCreate"
        >
          <AppIcon icon="mdi-plus" :size="16" />
          새 버전
        </button>
      </div>

      <p v-if="errorMessage" class="aldoc__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ errorMessage }}
      </p>

      <div v-if="docs.length" class="aldoc__table-wrap">
        <table class="aldoc__table">
          <thead>
            <tr>
              <th>코드</th>
              <th>버전</th>
              <th>제목</th>
              <th>구분</th>
              <th>대상</th>
              <th>시행일</th>
              <th>상태</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <tr v-for="doc in docs" :key="`${doc.code}-${doc.version}`">
              <td><code>{{ doc.code }}</code></td>
              <td>{{ doc.version }}</td>
              <td>{{ doc.title }}</td>
              <td>{{ kindLabel(doc.kind) }}</td>
              <td>{{ audienceLabel(doc.audience) }}</td>
              <td>{{ doc.effective }}</td>
              <td>
                <span class="sui-badge" :class="doc.active ? 'sui-badge--green' : 'sui-badge--gray'">
                  {{ doc.active ? '활성' : '비활성' }}
                </span>
              </td>
              <td>
                <button type="button" class="sui-btn sui-btn--sm" @click="openEdit(doc)">편집</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else-if="!loading" class="aldoc__empty">등록된 약관이 없습니다.</p>
    </section>

    <div v-if="modalOpen" class="aldoc__modal" role="dialog" aria-modal="true">
      <div class="aldoc__modal-backdrop" @click="closeModal" />
      <div class="aldoc__modal-panel" data-tour="admin-legal-docs-editor">
        <header>
          <h2>{{ editing ? '약관 편집' : '약관 새 버전' }}</h2>
          <button type="button" class="sui-btn sui-btn--sm" @click="closeModal">닫기</button>
        </header>

        <div class="aldoc__form">
          <label>
            <span>코드</span>
            <input v-model.trim="form.code" :readonly="editing" placeholder="예: seller_service" />
          </label>
          <label>
            <span>버전</span>
            <input v-model.trim="form.version" :readonly="editing" placeholder="예: 2026.08.10" />
          </label>
          <label>
            <span>대상</span>
            <select v-model="form.audience" :disabled="editing">
              <option value="SELLER">판매업체</option>
              <option value="MEMBER">일반회원</option>
              <option value="ALL">공통</option>
            </select>
          </label>
          <label>
            <span>구분</span>
            <select v-model="form.kind">
              <option value="TERMS">이용약관</option>
              <option value="CONTRACT">계약서</option>
            </select>
          </label>
          <label>
            <span>시행일</span>
            <input v-model="form.effective" type="date" />
          </label>
          <label class="is-full">
            <span>제목</span>
            <input v-model.trim="form.title" maxlength="200" />
          </label>
          <label class="is-full">
            <span>본문</span>
            <textarea v-model="form.body" rows="16" />
          </label>
          <label class="aldoc__check">
            <input v-model="form.active" type="checkbox" />
            이 버전을 활성으로 사용
          </label>
        </div>

        <footer>
          <button type="button" class="sui-btn" @click="closeModal">취소</button>
          <button type="button" class="sui-btn sui-btn--primary" :disabled="saving" @click="onSave">
            {{ saving ? '저장 중…' : '저장' }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  createAdminLegalDoc,
  fetchAdminLegalDocs,
  updateAdminLegalDoc,
  type LegalDoc,
} from '@/api/admin'

const docs = ref<LegalDoc[]>([])
const audience = ref('')
const loading = ref(false)
const saving = ref(false)
const errorMessage = ref('')
const modalOpen = ref(false)
const editing = ref(false)

const form = reactive({
  code: '',
  version: '',
  title: '',
  body: '',
  audience: 'SELLER' as string,
  kind: 'TERMS' as 'TERMS' | 'CONTRACT',
  effective: new Date().toISOString().slice(0, 10),
  active: true,
})

function audienceLabel(value: string) {
  if (value === 'SELLER') return '판매업체'
  if (value === 'MEMBER') return '일반회원'
  return '공통'
}

function kindLabel(value?: string) {
  return value === 'CONTRACT' ? '계약서' : '이용약관'
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    docs.value = await fetchAdminLegalDocs(audience.value)
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '약관을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function openCreate() {
  editing.value = false
  form.code = 'seller_service'
  form.version = new Date().toISOString().slice(0, 10).replace(/-/g, '.')
  form.title = ''
  form.body = ''
  form.audience = 'SELLER'
  form.kind = 'TERMS'
  form.effective = new Date().toISOString().slice(0, 10)
  form.active = true
  modalOpen.value = true
}

function openEdit(doc: LegalDoc) {
  editing.value = true
  form.code = doc.code
  form.version = doc.version
  form.title = doc.title
  form.body = doc.body
  form.audience = doc.audience
  form.kind = doc.kind === 'CONTRACT' ? 'CONTRACT' : 'TERMS'
  form.effective = doc.effective
  form.active = doc.active
  modalOpen.value = true
}

function closeModal() {
  modalOpen.value = false
}

async function onSave() {
  if (!form.code || !form.version || !form.title || !form.body) {
    window.alert('코드·버전·제목·본문을 입력해 주세요.')
    return
  }
  saving.value = true
  try {
    if (editing.value) {
      await updateAdminLegalDoc(form.code, form.version, {
        title: form.title,
        body: form.body,
        audience: form.audience,
        kind: form.kind,
        effective: form.effective,
        active: form.active,
      })
    } else {
      await createAdminLegalDoc({
        code: form.code,
        version: form.version,
        title: form.title,
        body: form.body,
        audience: form.audience,
        kind: form.kind,
        effective: form.effective,
        active: form.active,
      })
    }
    modalOpen.value = false
    await load()
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '저장에 실패했습니다.')
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style lang="scss" src="./AdminLegalDocsPage.scss"></style>
